import { CAMERA_OPACITY } from './visualSettings'
import type { LoopLayerState } from '../domain/types'
import { coverRect } from './trackingOverlay'

export interface CaptureCompositor {
  start(): void
  stop(): void
}

export function createCaptureCompositor(canvas: HTMLCanvasElement, video: HTMLVideoElement, overlay: HTMLCanvasElement, getLayers: () => readonly LoopLayerState[], visual?: HTMLCanvasElement): CaptureCompositor {
  const context = canvas.getContext('2d')
  let animationFrame = 0
  let running = false
  let lastDrawAt = -Infinity

  const draw = (timestampMs: number): void => {
    if (!running || !context) return
    if (timestampMs - lastDrawAt < 1000 / 30 - 1) { animationFrame = requestAnimationFrame(draw); return }
    lastDrawAt = timestampMs
    syncCaptureSize(canvas, overlay)
    const width = canvas.width
    const height = canvas.height
    context.fillStyle = '#050709'
    context.fillRect(0, 0, width, height)
    if (video.readyState >= 2 && video.videoWidth > 0) {
      const rect = coverRect(width, height, video.videoWidth, video.videoHeight)
      context.save()
      context.globalAlpha = CAMERA_OPACITY
      context.translate(rect.x + rect.width, rect.y)
      context.scale(-1, 1)
      context.drawImage(video, 0, 0, rect.width, rect.height)
      context.restore()
    }
    if (visual && visual.width > 1 && visual.height > 1) context.drawImage(visual, 0, 0, width, height)
    context.drawImage(overlay, 0, 0, width, height)
    if (overlay.dataset.chrome !== 'hidden') drawLayers(context, width, height, getLayers())
    animationFrame = requestAnimationFrame(draw)
  }

  return {
    start(): void {
      if (running) return
      running = true
      lastDrawAt = -Infinity
      syncCaptureSize(canvas, overlay)
      animationFrame = requestAnimationFrame(draw)
    },
    stop(): void { running = false; cancelAnimationFrame(animationFrame) }
  }
}

function syncCaptureSize(canvas: HTMLCanvasElement, overlay: HTMLCanvasElement): void {
  const aspect = overlay.width / Math.max(1, overlay.height)
  const width = aspect >= 1 ? 1280 : Math.max(1, Math.round(1280 * aspect))
  const height = aspect >= 1 ? Math.max(1, Math.round(1280 / aspect)) : 1280
  if (canvas.width !== width) canvas.width = width
  if (canvas.height !== height) canvas.height = height
}

function drawLayers(context: CanvasRenderingContext2D, width: number, height: number, layers: readonly LoopLayerState[]): void {
  const gap = 8
  const margin = 28
  const itemWidth = (width - margin * 2 - gap * 3) / 4
  const top = height - 84
  layers.forEach((layer, index) => {
    const x = margin + index * (itemWidth + gap)
    context.fillStyle = 'rgba(6, 10, 13, .68)'
    context.fillRect(x, top, itemWidth, 50)
    context.strokeStyle = layer.status === 'recording' ? '#ff6b58' : layer.status === 'empty' ? 'rgba(255,255,255,.18)' : index === 0 ? '#f4bd62' : '#55d5e8'
    context.strokeRect(x, top, itemWidth, 50)
    context.fillStyle = '#f5f1e9'
    context.font = '15px ui-monospace, monospace'
    context.fillText(`L${index + 1}  ${layer.status.toUpperCase()}`, x + 12, top + 29)
  })
}
